import { useMemo, useState } from "react";
import Reveal from "@/components/base/Reveal";
import SectionHeading from "@/components/base/SectionHeading";
import {
  diagnosisSteps,
  procedureGuides,
  repaymentGuide,
} from "@/mocks/siteData";

type DiagnosisSectionProps = {
  onOpenConsult: () => void;
  onOpenConsultWithMessage?: (message: string) => void;
};

type DiagnosisOption = (typeof diagnosisSteps)[number]["options"][number];

type DiagnosisResult = {
  level: string;
  title: string;
  desc: string;
  track: "rehab" | "bankrupt";
};

// 선택지 id -> 옵션(가중치) 매핑. 진단 점수 계산에 사용한다.
const OPTION_MAP: Map<string, DiagnosisOption> = new Map(
  diagnosisSteps.flatMap((step) =>
    step.options.map((option) => [option.id, option] as const),
  ),
);

/**
 * 선택한 항목의 개수와 위험 신호(압류·추심, 소득 단절)를 함께 반영해 등급을 산정한다.
 * - 위험 신호가 뚜렷하면 즉시 상담 등급으로 우선 처리
 * - 그 외에는 위험 항목 개수(riskCount) 기준으로 등급을 세분화
 */
function buildResult(answers: string[][]): DiagnosisResult {
  const flat = answers.flat();
  const hasSeizure =
    flat.includes("seizure-salary") || flat.includes("seizure-asset");
  const noIncome = flat.includes("income-none");
  const riskCount = flat.filter(
    (id) => (OPTION_MAP.get(id)?.weight ?? 0) >= 1,
  ).length;

  if (hasSeizure && noIncome) {
    return {
      level: "즉시 상담 권장",
      title: "개인파산까지 함께 검토해야 하는 상황입니다",
      desc: "압류·추심과 소득 단절이 함께 나타나고 있습니다. 개인회생·파산 중 어떤 절차가 유리한지 지금 바로 진단이 필요합니다.",
      track: "bankrupt",
    };
  }
  if (hasSeizure) {
    return {
      level: "즉시 상담 권장",
      title: "금지·중지명령으로 추심을 먼저 멈춰야 합니다",
      desc: "급여·재산 압류 또는 추심이 진행 중입니다. 접수와 동시에 금지명령을 신청하면 추심을 신속히 중단할 수 있습니다.",
      track: "rehab",
    };
  }
  if (noIncome) {
    return {
      level: "우선 상담 필요",
      title: "개인파산 적합 여부를 확인해 보세요",
      desc: "뚜렷한 소득이 거의 없는 경우 개인파산이 더 적합할 수 있습니다. 재산·채무 구조를 함께 검토해야 합니다.",
      track: "bankrupt",
    };
  }
  if (riskCount >= 5) {
    return {
      level: "즉시 상담 권장",
      title: "개인회생 준비를 더 늦추면 위험한 단계입니다",
      desc: "위험 신호가 여러 항목에서 겹치고 있습니다. 변제 능력이 남아 있는 지금이 신청 적기일 수 있습니다.",
      track: "rehab",
    };
  }
  if (riskCount >= 3) {
    return {
      level: "우선 상담 필요",
      title: "개인회생을 진지하게 검토해 볼 시점입니다",
      desc: "소득 대비 채무 부담이 큰 상태로 보입니다. 감당 가능한 변제안을 설계하면 충분히 재기할 수 있습니다.",
      track: "rehab",
    };
  }
  if (riskCount >= 1) {
    return {
      level: "상담 권장",
      title: "지금 상태를 전문가와 정확히 점검해 보세요",
      desc: "일부 위험 신호가 감지되었습니다. 방치하면 빠르게 커질 수 있으니 변제 계획을 미리 세워두는 것이 좋습니다.",
      track: "rehab",
    };
  }
  return {
    level: "가벼운 점검",
    title: "아직 큰 위험 신호는 없어 보입니다",
    desc: "현재는 채무 부담이 크지 않은 상태로 보입니다. 다만 상황이 바뀔 수 있으니 주기적으로 점검해 보세요.",
    track: "rehab",
  };
}

export default function DiagnosisSection({
  onOpenConsult,
  onOpenConsultWithMessage,
}: DiagnosisSectionProps) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<string[][]>(
    diagnosisSteps.map(() => []),
  );
  const [showResult, setShowResult] = useState(false);

  const current = diagnosisSteps[step];
  const selected = answers[step];
  const totalSelected = selected.length;

  const result = useMemo(() => buildResult(answers), [answers]);
  const procedure = procedureGuides[result.track];

  const toggle = (optionId: string) => {
    setAnswers((prev) =>
      prev.map((arr, i) =>
        i === step
          ? arr.includes(optionId)
            ? arr.filter((o) => o !== optionId)
            : [...arr, optionId]
          : arr,
      ),
    );
  };

  const reset = () => {
    setAnswers(diagnosisSteps.map(() => []));
    setStep(0);
    setShowResult(false);
  };

  // 자가진단 결과를 상담예약 폼 문의내용으로 넘길 요약 텍스트 생성
  const buildSummary = () => {
    const lines = diagnosisSteps
      .map((s, i) => {
        const labels = answers[i]
          .map((id) => OPTION_MAP.get(id)?.label ?? id)
          .join(", ");
        return answers[i].length ? `- ${s.short}: ${labels}` : "";
      })
      .filter(Boolean);
    return `[자가진단 결과] ${result.level} · ${result.title}\n${lines.join("\n")}`;
  };

  const handleResultConsult = () => {
    if (onOpenConsultWithMessage) {
      onOpenConsultWithMessage(buildSummary());
    } else {
      onOpenConsult();
    }
  };

  const progress = showResult ? 100 : ((step + 1) / diagnosisSteps.length) * 100;

  return (
    <section id="diagnosis" className="bg-background-50 py-20 md:py-28">
      <div className="mx-auto w-full max-w-3xl px-4 md:px-6">
        <Reveal>
          <SectionHeading
            kicker="Self Check"
            title="1분 개인회생 자가진단"
            description="해당하는 항목을 선택하면 즉시 진단 결과를 안내해 드립니다."
          />
        </Reveal>

        <Reveal delay={100}>
          <div className="mt-12 rounded-lg border border-background-200/70 bg-background-100/50 p-6 md:p-9">
            <div className="flex items-center justify-between">
              <span className="font-label text-xs text-primary-400">
                {showResult
                  ? "Result"
                  : `Step ${step + 1} / ${diagnosisSteps.length}`}
              </span>
              <span className="text-xs text-foreground-500">
                <b className="font-medium text-primary-400">{totalSelected}</b>개 선택
              </span>
            </div>

            <div className="mt-4 h-1 w-full overflow-hidden rounded-full bg-background-300/40">
              <div
                className="h-full rounded-full bg-primary-500 transition-all duration-500"
                style={{ width: `${progress}%` }}
              />
            </div>

            {!showResult ? (
              <>
                <h3 className="font-subheading mt-8 text-lg font-semibold text-foreground-950 md:text-xl">
                  {current.title}
                </h3>
                <div className="mt-6 grid grid-cols-1 gap-3 md:grid-cols-2">
                  {current.options.map((option) => {
                    const active = selected.includes(option.id);
                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => toggle(option.id)}
                        className={`flex items-center gap-3 rounded-md border px-4 py-3.5 text-left text-sm transition-colors duration-300 cursor-pointer ${
                          active
                            ? "border-primary-600 bg-primary-950/40 text-foreground-950"
                            : "border-background-200/70 bg-background-50 text-foreground-600 hover:border-primary-800/60"
                        }`}
                      >
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-sm border ${
                            active
                              ? "border-primary-500 bg-primary-500 text-background-50"
                              : "border-background-300/60"
                          }`}
                        >
                          {active ? <i className="ri-check-line text-xs" /> : null}
                        </span>
                        {option.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-8 flex items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200/70 px-5 py-3 text-sm text-foreground-600 transition-colors duration-300 hover:border-foreground-600/50 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <i className="ri-arrow-left-line" /> 이전
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (step === diagnosisSteps.length - 1) {
                        setShowResult(true);
                      } else {
                        setStep((s) => s + 1);
                      }
                    }}
                    disabled={totalSelected === 0}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors duration-300 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    {step === diagnosisSteps.length - 1 ? "결과 보기" : "다음"}
                    <i className="ri-arrow-right-line" />
                  </button>
                </div>
              </>
            ) : (
              <div className="mt-8 text-center">
                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-primary-700/60 bg-primary-950/50 text-primary-400">
                  <i className="ri-magic-line text-2xl" />
                </div>
                <span className="mt-5 inline-block rounded-full border border-accent-700/50 bg-accent-950/40 px-4 py-1 text-xs text-accent-300">
                  {result.level}
                </span>
                <h3 className="font-subheading mt-4 text-xl font-semibold text-foreground-950 md:text-2xl">
                  {result.title}
                </h3>
                <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-600">
                  {result.desc}
                </p>
                <p className="mx-auto mt-3 max-w-xl text-xs text-foreground-500">
                  ※ 자가진단은 참고용이며 법적 효력이 없습니다. 정확한 진단은 전문 변호사
                  상담을 통해 확인하세요.
                </p>
                <div className="mt-8 grid grid-cols-1 gap-4 text-left md:grid-cols-2">
                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md border border-primary-700/50 bg-primary-950/40 text-primary-400">
                        <i className="ri-wallet-3-line" />
                      </span>
                      <h4 className="font-subheading text-base font-semibold text-foreground-950">
                        {repaymentGuide.title}
                      </h4>
                    </div>
                    <p className="mt-3.5 text-sm font-medium text-primary-300">
                      {repaymentGuide.note}
                    </p>
                    <ul className="mt-3 space-y-2">
                      {repaymentGuide.points.map((point) => (
                        <li
                          key={point}
                          className="flex gap-2.5 text-xs leading-relaxed text-foreground-500"
                        >
                          <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-primary-500" />
                          <span>{point}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
                    <div className="flex items-center gap-2.5">
                      <span className="flex h-9 w-9 items-center justify-center rounded-md border border-accent-700/50 bg-accent-950/40 text-accent-300">
                        <i className="ri-route-line" />
                      </span>
                      <div>
                        <h4 className="font-subheading text-base font-semibold text-foreground-950">
                          {procedure.label}
                        </h4>
                        <span className="text-[11px] text-foreground-500">
                          {procedure.duration}
                        </span>
                      </div>
                    </div>
                    <ol className="mt-3.5 space-y-2">
                      {procedure.steps.map((item, i) => (
                        <li
                          key={item}
                          className="flex items-center gap-2.5 text-xs text-foreground-600"
                        >
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-accent-700/40 font-label text-[10px] text-accent-300">
                            {i + 1}
                          </span>
                          <span>{item}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
                <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleResultConsult}
                    className="inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-7 py-3 text-sm font-medium text-background-50 transition-colors duration-300 hover:bg-primary-600 cursor-pointer sm:w-auto"
                  >
                    <i className="ri-file-list-3-line mr-1.5" />
                    진단 결과로 상담예약
                  </button>
                  <button
                    type="button"
                    onClick={reset}
                    className="inline-flex w-full items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-200/70 px-7 py-3 text-sm text-foreground-600 transition-colors duration-300 hover:border-foreground-600/50 cursor-pointer sm:w-auto"
                  >
                    <i className="ri-refresh-line" /> 다시 진단하기
                  </button>
                </div>
              </div>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}